import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrderProvider } from './contexts/OrderContext';
import { PageTitleProvider } from './contexts/PageTitleContext';
import { UserSettingsProvider } from './contexts/UserSettingsContext';
import ProtectedRoute from './components/layout/ProtectedRoute';
import Layout from './components/layout/Layout';
import OrdersList from './components/orders/OrdersList';
import ShelfIndexer from './components/labels/ShelfIndexer';
import SearchWarehouse from './components/warehouse/SearchWarehouse';
import OrderDetail from './components/orders/orderdetail/OrderDetail';
import BundleWorkOrderDetail from './components/orders/orderdetail/BundleWorkOrderDetail';
import LandingPage from './components/layout/LandingPage';
import OrdersPilot from './components/orders/OrdersPilot';
import MyOrdersList from './components/orders/MyOrdersList';
import NextOrderPicker from './components/orders/NextOrderPicker';
import ClaimOrderScanner from './components/orders/ClaimOrderScanner';
import GeneralSettings from './components/admin/GeneralSettings';
import SingleSkuOrdersList from './components/orders/SingleSkuOrdersList';
import BundleWorkOrdersList from './components/orders/BundleWorkOrdersList';
import InventoryCounts from './components/inventory/InventoryCounts';
import InventoryTransfersList from './components/inventory/InventoryTransfersList';
import InventoryTransferDetail from './components/inventory/InventoryTransferDetail';
import PrintBoxLabel from './components/labels/PrintBoxLabel';
import PrintWarehouseLabels from './components/labels/PrintWarehouseLabels';
import CheckLabelOut from './components/labels/CheckLabelOut';
import PrintOrdersList from './components/labels/PrintOrdersList';
import Filter from './components/layout/Filter';
import DevPanel from './components/layout/DevPanel';
import ShelfAudit from './components/dev/ShelfAudit';
import InvDiffReview from './components/dev/InvDiffReview';
import OcrAbTest from './components/dev/OcrAbTest';
import ShipmentsList from './components/receiving/ShipmentsList';
import ShipmentDetail from './components/receiving/ShipmentDetail';
import ShipmentItemDetail from './components/receiving/ShipmentItemDetail';
import AdminPanel from './components/admin/AdminPanel';
import AdminDashboard from './components/admin/AdminDashboard';
import PastPerformance from './components/admin/PastPerformance';
import ShelfManage from './components/admin/ShelfManage';
import BundleScanSubsetManage from './components/admin/BundleScanSubsetManage';
import BundleDefaultsManage from './components/admin/BundleDefaultsManage';
import BundleFloorManage from './components/admin/BundleFloorManage';
import SpaceCalculator from './components/tools/SpaceCalculator';
import ScanLabel from './components/labels/ScanLabel';
import ProductWeight from './components/warehouse/ProductWeight';
import ConfigureAccount from './components/account/ConfigureAccount';
import TaskQueuePage from './components/tasks/TaskQueuePage';
import TaskDetailPage from './components/tasks/TaskDetailPage';
import TaskCreatePage from './components/tasks/TaskCreatePage';
import TaskTagsAdmin from './components/tasks/TaskTagsAdmin';
import RecurringDefsPage from './components/tasks/RecurringDefsPage';

interface RequireDevProps {
    children: React.ReactNode;
}

const RequireDev: React.FC<RequireDevProps> = ({ children }) => {
    const { isDev } = useAuth();
    if (!isDev) return <Navigate to="/" replace />;
    return <>{children}</>;
};

const RequireAdmin: React.FC<RequireDevProps> = ({ children }) => {
    const { isAdmin } = useAuth();
    if (!isAdmin) return <Navigate to="/" replace />;
    return <>{children}</>;
};

const App: React.FC = () => {
    return (
        <AuthProvider>
            <OrderProvider>
                <PageTitleProvider>
                    <ProtectedRoute>
                        <UserSettingsProvider>
                            <Layout>
                            <Routes>
                            <Route path="/" element={<LandingPage />} />
                            <Route path="/configure" element={<ConfigureAccount />} />
                            <Route path="/tasks" element={<TaskQueuePage />} />
                            <Route path="/tasks/new" element={<TaskCreatePage />} />
                            <Route path="/tasks/:id" element={<TaskDetailPage />} />
                            <Route path="/admin/task-tags" element={<RequireAdmin><TaskTagsAdmin /></RequireAdmin>} />
                            <Route path="/admin/recurring-tasks" element={<RequireAdmin><RecurringDefsPage /></RequireAdmin>} />
                            <Route path="/order-manager" element={<OrdersPilot />} />
                            <Route path="/orders-list" element={<OrdersList />} />
                            <Route path="/my-orders" element={<MyOrdersList />} />
                            <Route path="/single-sku-orders" element={<SingleSkuOrdersList />} />
                            <Route path="/bundle-work-orders" element={<BundleWorkOrdersList />} />
                            <Route path="/pick-next-order" element={<NextOrderPicker />} />
                            <Route path="/claim-order" element={<ClaimOrderScanner />} />
                            <Route path="/scan-label" element={<ScanLabel />} />
                            <Route path="/product-weight" element={<ProductWeight />} />
                            <Route path="/shelf-indexer" element={<ShelfIndexer />} />
                            <Route path="/search-warehouse" element={<SearchWarehouse />} />
                            <Route path="/order/:orderId" element={<OrderDetail />} />
                            <Route path="/bundle-work-order/:parentSku" element={<BundleWorkOrderDetail />} />
                            <Route path="/filter" element={<Filter />} />
                            <Route path="/dev" element={<RequireDev><DevPanel /></RequireDev>} />
                            <Route path="/dev/inv-diff" element={<RequireDev><InvDiffReview /></RequireDev>} />
                            <Route path="/dev/ocr-ab" element={<RequireDev><OcrAbTest /></RequireDev>} />
                            <Route path="/shelf-audit" element={<ShelfAudit />} />
                            <Route path="/inventory-counts" element={<InventoryCounts />} />
                            <Route path="/inventory-transfers" element={<InventoryTransfersList />} />
                            <Route path="/inventory-transfers/:transferId" element={<InventoryTransferDetail />} />
                            <Route path="/print-warehouse-labels" element={<PrintWarehouseLabels />} />
                            <Route path="/print-warehouse-labels/print" element={<PrintBoxLabel />} />
                            <Route path="/print-warehouse-labels/check-label-out" element={<CheckLabelOut />} />
                            <Route path="/print-warehouse-labels/reprint" element={<PrintOrdersList />} />
                            <Route path="/admin" element={<RequireAdmin><AdminPanel /></RequireAdmin>} />
                            <Route path="/admin/general-settings" element={<RequireAdmin><GeneralSettings /></RequireAdmin>} />
                            <Route path="/admin/dashboard" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
                            <Route path="/admin/past-performance" element={<RequireAdmin><PastPerformance /></RequireAdmin>} />
                            <Route path="/admin/shelf-manage" element={<RequireAdmin><ShelfManage /></RequireAdmin>} />
                            <Route path="/admin/bundle-scan-subset" element={<RequireAdmin><BundleScanSubsetManage /></RequireAdmin>} />
                            <Route path="/admin/bundle-defaults" element={<RequireAdmin><BundleDefaultsManage /></RequireAdmin>} />
                            <Route path="/admin/bundle-thresholds" element={<RequireAdmin><BundleFloorManage /></RequireAdmin>} />
                            <Route path="/space-calculator" element={<SpaceCalculator />} />
                            <Route path="/receive" element={<ShipmentsList />} />
                            <Route path="/receive/shipment/:shipmentId" element={<ShipmentDetail />} />
                            <Route path="/receive/shipment/:shipmentId/item/:shipmentItemId" element={<ShipmentItemDetail />} />
                            </Routes>
                            </Layout>
                        </UserSettingsProvider>
                    </ProtectedRoute>
                </PageTitleProvider>
            </OrderProvider>
        </AuthProvider>
    );
};

export default App;
